import React from 'react';
import { useNavigate } from 'react-router-dom';
import { buildAppPath } from '@/utils/tenant';
import styles from './AccountSelection.module.css';

/**
 * Account Selection Component
 * Landing page for users to choose between B2B (Loan Officer) and B2C (Consumer) login
 */
const AccountSelection = () => {
  const navigate = useNavigate();

  /**
   * Navigate to B2B login
   */
  const handleB2BLogin = () => {
    navigate(buildAppPath('/login/b2b'));
  };

  /**
   * Navigate to Consumer login
   */
  const handleConsumerLogin = () => {
    navigate(buildAppPath('/login/consumer'));
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logoContainer}>
          <img 
            src="/mira/mira_logo.png" 
            alt="MIRA Labs"
            className={styles.logo}
          />
        </div>
        
        <div className={styles.header}>
          <h1 className={styles.title}>Welcome to MIRA Labs</h1>
          <p className={styles.subtitle}>
            Choose your account type to get started
          </p>
        </div>

        <div className={styles.optionsContainer}>
          {/* B2B Login Option */}
          <div 
            className={styles.optionCard}
            onClick={handleB2BLogin}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleB2BLogin();
              }
            }}
            aria-label="Login as Loan Officer or Organization Employee"
          >
            <div className={styles.optionIcon}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H7m5 0v-5a2 2 0 011.414-1.414L15 13l1.414 1.414A2 2 0 0118 16v5m-5-5V8a2 2 0 00-2-2H9a2 2 0 00-2 2v8z" />
              </svg>
            </div>
            <div className={styles.optionContent}>
              <h2 className={styles.optionTitle}>Loan Officer</h2>
              <p className={styles.optionDescription}>
                For mortgage professionals and organization employees
              </p>
              <ul className={styles.optionFeatures}>
                <li>Multi-tenant organization support</li>
                <li>Advanced loan management tools</li>
                <li>Team collaboration features</li>
                <li>NMLS compliance tracking</li>
              </ul>
            </div>
            <div className={styles.optionArrow}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Consumer Login Option */}
          <div 
            className={styles.optionCard}
            onClick={handleConsumerLogin}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleConsumerLogin();
              }
            }}
            aria-label="Login as Consumer or Borrower"
          >
            <div className={styles.optionIcon}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className={styles.optionContent}>
              <h2 className={styles.optionTitle}>Consumer</h2>
              <p className={styles.optionDescription}>
                For borrowers and individual mortgage applicants
              </p>
              <ul className={styles.optionFeatures}>
                <li>Personal loan applications</li>
                <li>Document upload and tracking</li>
                <li>Application status updates</li>
                <li>Secure communication</li>
              </ul>
            </div>
            <div className={styles.optionArrow}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className={styles.securityNotice}>
          <svg className={styles.securityIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div className={styles.securityText}>
            <strong>Enterprise Security:</strong> All connections are secured with 
            end-to-end encryption and comply with financial industry regulations.
          </div>
        </div>

        {/* Platform Admin Link */}
        <div className={styles.platformLink}>
          <button 
            type="button"
            onClick={() => navigate(buildAppPath('/platform/login'))}
            className={styles.platformButton}
            aria-label="Platform Administration Login"
          >
            Platform Admin Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSelection;
